import {
  AddressingDataItem,
  AddressingEntity,
  AddressingEntityType,
  AddressingRule,
  AddressingRuleLevel,
  ExtendedAddressingRule,
  ExtendedPlayerAddressingRule,
  NormalizedAddressingEntity,
  NormalizedAddressingRule,
  PlayerAddressingRule
} from '../store';

interface NormalizedEntities {
  [entityType: number]: NormalizedAddressingEntity;
}

export const transformBackendData = (
  data: AddressingDataItem[]
): {
  groups: NormalizedAddressingEntity;
  sites: NormalizedAddressingEntity;
  players: NormalizedAddressingEntity;
  streams: NormalizedAddressingEntity;
  addressingRules: NormalizedAddressingRule;
} => {
  const entities = normalizeData(data);
  const normalizedEntities: NormalizedEntities = {
    [AddressingEntityType.Group]: entities.groups,
    [AddressingEntityType.Site]: entities.sites,
    [AddressingEntityType.Player]: entities.players,
    [AddressingEntityType.Stream]: entities.streams
  };
  const rules = normalizeRules(normalizedEntities);
  return { ...entities, addressingRules: rules };
};

const normalizeData = (
  data: AddressingDataItem[]
): {
  groups: NormalizedAddressingEntity;
  sites: NormalizedAddressingEntity;
  players: NormalizedAddressingEntity;
  streams: NormalizedAddressingEntity;
} => {
  const sites = data
    .filter(item => item.entityType === AddressingEntityType.Site)
    .reduce((obj, item) => {
      const site: AddressingEntity = entityFromDataItem(item);
      obj[item.id] = site;
      return obj;
    }, {});
  const groups = data
    .filter(item => item.entityType === AddressingEntityType.Group)
    .reduce((obj, item) => {
      const group: AddressingEntity = {...entityFromDataItem(item), hide: item.allLoc};
      group.children = parseStringForValues(item.val).filter(siteId => sites[siteId] !== undefined);
      obj[item.id] = group;
      return obj;
    }, {});
  const players = data
    .filter(item => item.entityType === AddressingEntityType.Player)
    .reduce((obj, item) => {
      const player: AddressingEntity = entityFromDataItem(item);
      const siteId = Number(item.val);
      if (!Number.isNaN(siteId)) {
        sites[siteId].children.push(item.id);
      }
      obj[item.id] = player;
      return obj;
    }, {});
  const streams = data
    .filter(item => item.entityType === AddressingEntityType.Stream)
    .reduce((obj, item) => {
      const player: AddressingEntity = entityFromDataItem(item);
      const playerId = Number(item.val);
      if (!Number.isNaN(playerId)) {
        players[playerId].children.push(item.id);
      }
      obj[item.id] = player;
      return obj;
    }, {});

  return { groups, sites, players, streams };
};

const parseStringForValues = (val: string): number[] => {
  if (!val) {
    return [];
  }
  return val
    .split(',')
    .map(s => Number(s.trim()))
    .filter(n => !Number.isNaN(n));
};

const entityFromDataItem = (item: AddressingDataItem): AddressingEntity => ({
  id: item.id,
  name: item.name,
  type: item.entityType,
  denyMediaId: parseStringForValues(item.denyMediaId),
  allowMediaId: parseStringForValues(item.allowMediaId),
  children: []
});

const normalizeRules = (entities: NormalizedEntities): NormalizedAddressingRule => {
  let rules: ExtendedAddressingRule = {};
  const groups = entities[AddressingEntityType.Group];
  
  for (const groupId of Object.keys(groups)) {
    const addRules = rulesForGroup(groups[groupId], entities);

    Object.keys(addRules).forEach((playerId: string) => {
      // if there's already a player with this id in the rule set,
      // concat the new denies/allows arrays to the already existing array
      if (Object.keys(rules).indexOf(playerId) > -1) {
        rules[playerId].denies = [...rules[playerId].denies, ...addRules[playerId].denies];
        rules[playerId].allows = [...rules[playerId].allows, ...addRules[playerId].allows];
      } else {
        // otherwise just add the new playerId to the rules object
        rules = {...rules, ...addRules};
      }
    });
  }
  
  return filterRulesByLevel(rules);
};

// process allow and deny rules for each playerId and compute the final denies array
const filterRulesByLevel = (rules: ExtendedAddressingRule): NormalizedAddressingRule => {
  // iterate by playerId and reduce to a NormalizedAddressingRule instance
  return Object.keys(rules).reduce((result: PlayerAddressingRule, playerId: string) => {
    let allows: AddressingRule[] = rules[playerId].allows;
    let denies: AddressingRule[] = rules[playerId].denies;

    // group all addressing rules from an array by mediaId and only retain the highest AddressingRuleLevel
    const processAddressingRules = (addressingRules: AddressingRule[]) => {
      
      addressingRules = addressingRules.filter((addressingRule: AddressingRule) => {
        const mediaId = addressingRule.mediaId;
        const level = addressingRule.level;
  
        return !addressingRules.some((tempAddressingRule: AddressingRule) => {
  
          if ((tempAddressingRule.mediaId === mediaId) && (tempAddressingRule.level > level)) {
            return true;
          }
  
          return false;
        });
      });

      const uniqueMediaIds: number[] = [];

      // after selecting the highest AddressingRuleLevel for all mediaIds, filter out unique values
      const uniquqMedias = (addressingRule: AddressingRule) => {
        if (uniqueMediaIds.indexOf(addressingRule.mediaId) < 0) {
          uniqueMediaIds.push(addressingRule.mediaId);
          return true;
        }

        return false;
      };

      return addressingRules.filter(uniquqMedias);
    };
    
    // using the above logic, process both allows and denies for the current playerId
    allows = processAddressingRules(allows);
    denies = processAddressingRules(denies);

    // now remove the allows with higher (or equal) AddressingRuleLevel from the denied
    denies = denies.filter((denyAddressingRule: AddressingRule) => {
      const denyLevel = denyAddressingRule.level;
      const denyMediaId = denyAddressingRule.mediaId;

      return !allows.some((allowAddressingRule: AddressingRule) => {
        const allowLevel = allowAddressingRule.level;
        const allowMediaId = allowAddressingRule.mediaId;

        if ((allowMediaId === denyMediaId) && (allowLevel >= denyLevel)) {
          return true;
        }

        return false;
      });
    });

    // if there are no deny rules for this playerId just return the previous result
    if (!denies.length) {
      return result;
    }

    const rule: PlayerAddressingRule = {
      entityId: Number(playerId).valueOf(),
      entityType: rules[playerId].entityType,
      denies: denies.map((denyAddressingRule: AddressingRule) => denyAddressingRule.mediaId).filter((value, index, self) => self.indexOf(value) === index)
    };    

    // add the new deny rules to the ruleset object
    result[playerId] = rule;
    return result;
  }, {});
};

const rulesForGroup = (group: AddressingEntity, entities: NormalizedEntities): ExtendedAddressingRule => {
  let playerRules: ExtendedPlayerAddressingRule[] = [];
  const {allows, denies} = rulesForWhateverEntity(group, [], []);

  for (const siteId of group.children) {
    const site = entities[AddressingEntityType.Site][siteId];
    playerRules = { ...playerRules, ...rulesForSite(site, denies, allows, entities) };
  }
  return playerRules;
};

const rulesForSite = (
  site: AddressingEntity,
  deniedMediaIdsFromGroup: AddressingRule[],
  alloweddMediaIdsFromGroup: AddressingRule[],
  entities: NormalizedEntities
): ExtendedAddressingRule => {
  let playerRules: ExtendedAddressingRule = {};
  const {allows, denies} = rulesForWhateverEntity(site, deniedMediaIdsFromGroup, alloweddMediaIdsFromGroup);

  for (const playerId of site.children) {
    const player = entities[AddressingEntityType.Player][playerId];
    playerRules = { ...playerRules, ...rulesForPlayer(player, denies, allows, entities) };
  }
  return playerRules;
};

const rulesForPlayer = (
  player: AddressingEntity,
  deniedMediaIdsFromSite: AddressingRule[],
  alloweddMediaIdsFromSite: AddressingRule[],
  entities: NormalizedEntities
): ExtendedAddressingRule => {
  const {allows: playerAllows, denies: playerDenies} = rulesForWhateverEntity(player, deniedMediaIdsFromSite, alloweddMediaIdsFromSite);
  let streamAllows: AddressingRule[] = playerAllows;
  let streamDenies: AddressingRule[] = playerDenies;
  let result;

  for (const streamId of player.children) {
    const stream = entities[AddressingEntityType.Stream][streamId];
    result = rulesForWhateverEntity(stream, playerDenies, playerAllows);
    streamAllows = [...streamAllows, ...result.allows];
    streamDenies = [...streamDenies, ...result.denies];
  }  

  return {
    [player.id]: {
      allows: streamAllows,
      denies: streamDenies,
      entityId: player.id,
      entityType: AddressingEntityType.Player
    }
  };
};

const entityTypeToRuleLevel = (entityType: AddressingEntityType): AddressingRuleLevel => {
  switch (entityType) {
    case AddressingEntityType.Group: return AddressingRuleLevel.Group;
    case AddressingEntityType.Site: return AddressingRuleLevel.Site;
    case AddressingEntityType.Player: return AddressingRuleLevel.Player;
    case AddressingEntityType.Stream: return AddressingRuleLevel.Stream;
    default: return AddressingRuleLevel.Group;
  }
}

const rulesForWhateverEntity = (entity: AddressingEntity, deniedMediaIds: AddressingRule[], allowMediaIds: AddressingRule[]) => {
  const denies = [...deniedMediaIds];
  const allows = [...allowMediaIds];
  const ruleLevel = entityTypeToRuleLevel(entity.type);
  
  entity.denyMediaId.forEach(denyId => {
     denies.push({mediaId: denyId, level: ruleLevel});
  });
  entity.allowMediaId.forEach(allowId => {
    allows.push({mediaId: allowId, level: ruleLevel})
  });
  return {allows, denies};
};



// WEBPACK FOOTER //
// ./src/utils/adressingTransform.ts